import React, { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n/LangContext";

const SCRIPTS = {
  es: {
    reservas: [
      { from: "user", text: "Hola, quiero reservar mesa para 4 personas el sábado a las 8pm" },
      { from: "bot", text: "¡Hola! Soy Zen, la asistente de La Trattoria 🍝" },
      { from: "bot", text: "Confirmo: 4 personas · Sábado 14 · 8:00 PM. ¿Algún cumpleaños o alergia?" },
      { from: "user", text: "Sí, mi novia cumple años 🎂" },
      { from: "bot", text: "¡Qué bello! Te aparto la mesa junto a la ventana y le preparamos un postre cortesía. Mesa confirmada ✅" },
      { from: "bot", text: "Te llegará recordatorio el sábado al mediodía. ¿Algo más?", isLink: true },
    ],
    ventas: [
      { from: "user", text: "¿Cuánto cuesta una asesoría de marketing?" },
      { from: "bot", text: "¡Hola! Para darte el precio justo necesito 2 datos 🚀" },
      { from: "bot", text: "1. ¿Cuál es tu rubro?\n2. ¿Cuántos seguidores tienes hoy?" },
      { from: "user", text: "Restaurante, 1.200 seguidores en IG" },
      { from: "bot", text: "Perfecto. Tu plan ideal es Crece+ a $180/mes. Incluye 12 posts, gestión de WhatsApp y reportes." },
      { from: "bot", text: "Te agendo una demo gratis de 15 min. Elige horario 👇", isLink: true },
    ],
    soporte: [
      { from: "user", text: "Mi pedido no llegó 😟" },
      { from: "bot", text: "Lo lamento. Comparte tu número de orden y lo rastreo al instante." },
      { from: "user", text: "ORD-2451" },
      { from: "bot", text: "Tu pedido salió hoy 9:42 AM. Está a 3 paradas. Llegada estimada: 4:15 PM" },
      { from: "bot", text: "Te envío el link de seguimiento en vivo 📍", isLink: true },
      { from: "user", text: "¡Genial, mil gracias!" },
    ],
  },
  en: {
    reservas: [
      { from: "user", text: "Hi, I'd like to book a table for 4 on Saturday at 8pm" },
      { from: "bot", text: "Hi! I'm Zen, La Trattoria's assistant 🍝" },
      { from: "bot", text: "Confirming: 4 people · Saturday 14 · 8:00 PM. Any birthdays or allergies?" },
      { from: "user", text: "Yes, my girlfriend's birthday 🎂" },
      { from: "bot", text: "How lovely! I'll save you the window table and prepare a complimentary dessert. Table confirmed ✅" },
      { from: "bot", text: "You'll get a reminder Saturday at noon. Anything else?", isLink: true },
    ],
    ventas: [
      { from: "user", text: "How much is a marketing consultation?" },
      { from: "bot", text: "Hi! To give you a fair price I need 2 details 🚀" },
      { from: "bot", text: "1. What's your industry?\n2. How many followers do you have today?" },
      { from: "user", text: "Restaurant, 1,200 IG followers" },
      { from: "bot", text: "Perfect. Your ideal plan is Grow+ at $180/mo. Includes 12 posts, WhatsApp management and reports." },
      { from: "bot", text: "I'll book a free 15-min demo. Pick a time 👇", isLink: true },
    ],
    soporte: [
      { from: "user", text: "My order didn't arrive 😟" },
      { from: "bot", text: "I'm sorry. Share your order number and I'll track it instantly." },
      { from: "user", text: "ORD-2451" },
      { from: "bot", text: "Your order left today at 9:42 AM. It's 3 stops away. ETA: 4:15 PM" },
      { from: "bot", text: "Sending you the live tracking link 📍", isLink: true },
      { from: "user", text: "Awesome, thanks so much!" },
    ],
  },
};

/* Custom hook: reproduce a chat script as a sequence of messages with typing pauses */
const useChatPlayback = (script) => {
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setShown([]);
    setTyping(false);

    const playMessage = async (msg) => {
      if (msg.from === "bot") {
        setTyping(true);
        await new Promise((r) => setTimeout(r, 900));
        if (cancelled) return false;
        setTyping(false);
      } else {
        await new Promise((r) => setTimeout(r, 400));
      }
      if (cancelled) return false;
      setShown((prev) => [...prev, msg]);
      await new Promise((r) => setTimeout(r, 500));
      return true;
    };

    (async () => {
      for (const msg of script) {
        if (cancelled) return;
        const ok = await playMessage(msg);
        if (!ok) return;
      }
    })();

    return () => { cancelled = true; };
  }, [script]);

  return { shown, typing };
};

const WhatsAppDemo = () => {
  const { t, lang } = useLang();
  const TABS = t.demo.tabs;
  const [activeTab, setActiveTab] = useState("reservas");
  const script = SCRIPTS[lang]?.[activeTab] || SCRIPTS.es[activeTab];
  const { shown, typing } = useChatPlayback(script);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [shown, typing]);

  return (
    <div className="w-full max-w-sm mx-auto" data-testid="whatsapp-demo">
      <div className="flex gap-2 mb-4 justify-center" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            data-testid={`demo-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-[#00E5A0] text-[#0D1B2A] shadow-lg shadow-[#00E5A0]/30"
                : "bg-white/5 text-[#F5F7FA]/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            <span className="mr-1.5">{tab.emoji}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="relative mx-auto" style={{ width: "min(360px, 100%)" }}>
        <div className="bg-black rounded-[44px] p-3 shadow-[0_30px_80px_-20px_rgba(0,229,160,0.25)] border border-white/10">
          <div className="bg-[#0a1622] rounded-[34px] overflow-hidden h-[560px] flex flex-col">
            <div className="relative h-7 bg-black flex items-center justify-center">
              <div className="absolute top-1 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full border border-white/5" />
            </div>

            <div className="bg-[#00543C] px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#00E5A0] flex items-center justify-center text-[#0D1B2A] font-bold text-sm font-display">
                Z
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-semibold truncate">{t.demo.phone_name}</div>
                <div className="text-white/60 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E5A0]" />
                  {t.demo.phone_status}
                </div>
              </div>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-3 py-4 space-y-2"
              style={{ background: "linear-gradient(180deg, #0F1B14 0%, #0a1410 100%)" }}
            >
              {shown.map((m, idx) => (
                <div
                  key={idx}
                  className={`flex ${m.from === "user" ? "justify-end" : "justify-start"} animate-fade-up`}
                >
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-[13px] leading-snug whitespace-pre-line ${
                      m.from === "user"
                        ? "bg-[#005C4B] text-white rounded-br-sm"
                        : "bg-[#1F2C24] text-white rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                    {m.isLink && (
                      <button className="mt-2 block w-full text-center bg-[#00E5A0]/15 border border-[#00E5A0]/30 text-[#00E5A0] py-1.5 rounded-lg text-xs font-medium">
                        {t.demo.open_link}
                      </button>
                    )}
                    <div className="text-[10px] text-white/40 text-right mt-0.5">
                      {new Date().getHours()}:{String(new Date().getMinutes()).padStart(2, "0")}
                    </div>
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start animate-fade-up">
                  <div className="bg-[#1F2C24] px-4 py-3 rounded-2xl rounded-bl-sm">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-[#0a1410] px-3 py-2 flex items-center gap-2 border-t border-white/5">
              <div className="flex-1 bg-[#1F2C24] rounded-full px-3 py-2 text-xs text-white/40">
                {t.demo.phone_placeholder}
              </div>
              <div className="w-9 h-9 rounded-full bg-[#00E5A0] flex items-center justify-center text-[#0D1B2A]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute -inset-8 -z-10 bg-[#00E5A0]/10 blur-3xl rounded-full" />
      </div>

      <p className="text-center text-xs text-[#6B7A8D] mt-4 font-mono">
        {t.demo.hint}
      </p>
    </div>
  );
};

export default WhatsAppDemo;
